import { createAsyncThunk, ActionReducerMapBuilder } from "@reduxjs/toolkit";
import { AICodingClient, AICodingExecutionStatuses, AICodingJobDto, ExecuteAICodingJobCommand } from "app/V2ApiClient";
import { SortDirections, SorterDescriptor } from "app/ApiClient";
import { LoadingState } from "types";
import { AICodingState } from "./aiCodingState";
import { mapJobDetailsToVariableVM } from "./aiCodingMapper";
import { RootState } from "@store";
import saveAs from "file-saver";

const client = new AICodingClient(process.env.REACT_APP_API_URL);
const API_VERSION = "2";

export const getAICodingCodeframe = createAsyncThunk(
  "aiCodingV2/getAICodingCodeframe",
  async (jobConfigId: number, { rejectWithValue }) => {
    try {
      return await client.getAICodingCodeframe(jobConfigId, API_VERSION);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getCodeframeCodedCount = createAsyncThunk(
  "aiCodingV2/getCodeframeCodedCount",
  async (jobConfigId: number, { rejectWithValue }) => {
    try {
      return await client.getCodeframeCodedCount(jobConfigId, API_VERSION);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const executeAICodingJob = createAsyncThunk(
  "aiCodingV2/executeAICodingJob",
  async (command: ExecuteAICodingJobCommand, { rejectWithValue }) => {
    try {
      return await client.executeAICodingJob(API_VERSION, command);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getAICodingExecutionStatus = createAsyncThunk(
  "aiCodingV2/getAICodingExecutionStatus",
  async ({ token }: { token: string }, { rejectWithValue }) => {
    try {
      return await client.getAICodingExecutionStatus(token, API_VERSION);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getAICodingJob = createAsyncThunk(
  "aiCodingV2/getAICodingJob",
  async (jobConfigId: number, { getState, rejectWithValue }) => {
    try {
      const jobDetails = await client.getAICodingJobDetails(jobConfigId, API_VERSION);
      const { project } = getState() as RootState;
      const respondentsCount = !project.respondentsCountLoading && project.respondentsCount > 0
        ? project.respondentsCount
        : undefined;

      return {
        jobDetails,
        respondentsCount,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const sortAICodingJobs = (jobs: AICodingJobDto[], sortList?: SorterDescriptor[]): AICodingJobDto[] => {
  if (sortList && sortList.length > 0) {
    let sortedJobs: AICodingJobDto[] = [...jobs];
    for (const sortDesc of sortList) {
      const sortKey = sortDesc.field as keyof AICodingJobDto;
      sortedJobs = sortedJobs.sort((a, b) => {
        const dataA = a[sortKey]?.toString();
        const dataB = b[sortKey]?.toString();

        const sortDirection: SortDirections = sortDesc.directions ?? SortDirections.Desceding;

        if (dataA && dataB) {
          return sortDirection === SortDirections.Asceding ? dataA.localeCompare(dataB) : dataB.localeCompare(dataA);
        }
        if (dataA && !dataB) {
          return sortDirection === SortDirections.Asceding ? 1 : -1;
        }
        if (!dataA && dataB) {
          return sortDirection === SortDirections.Asceding ? -1 : 1;
        }
        return 0;
      });
    }
    return sortedJobs;
  }
  return jobs;
};

export const getAICodingJobs = createAsyncThunk(
  "aiCoding/getAICodingJobs",
  async (
    { projectId, encodedVersionId }: { projectId: string; encodedVersionId: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const { aiCoding } = getState() as RootState;
      const allJobs = await client.getAICodingJobs(projectId, encodedVersionId, "2");
      // Apply sorting only - filtering is done client-side in the component
      const sortedJobs = sortAICodingJobs(allJobs, aiCoding.pageContext.sortList);
      return sortedJobs;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const downloadCodeframeResults = createAsyncThunk(
  "aiCodingV2/downloadCodeframeResults",
  async (jobConfigId: number, { rejectWithValue }) => {
    try {
      const result = await client.downloadCodeframeResults(jobConfigId, API_VERSION);
      saveAs(result.data, result.fileName);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const addV2Reducers = (builder: ActionReducerMapBuilder<AICodingState>) => {
  builder
    .addCase(getAICodingJob.pending, (state, action) => {
      state.currentAICodingJobConfigId = action.meta.arg;
      state.aiCodingJobDetailsLoading = LoadingState.pending;
    })
    .addCase(getAICodingJob.fulfilled, (state, action) => {
      if (state.currentAICodingJobConfigId !== action.meta.arg) {
        return;
      }

      state.aiCodingJobDetailsLoading = LoadingState.succeed;
      state.aiCodingJobDetailsDto = action.payload.jobDetails;

      const cachedCodedCount = state.codeframeCodedCountByJobConfigId[action.meta.arg];
      state.variableItemVM = mapJobDetailsToVariableVM(
        action.payload.jobDetails,
        action.payload.respondentsCount,
        cachedCodedCount,
      );
      state.variableStatsLoading = LoadingState.idle;
    })
    .addCase(getAICodingJob.rejected, (state, action) => {
      if (state.currentAICodingJobConfigId !== action.meta.arg) {
        return;
      }

      state.aiCodingJobDetailsLoading = LoadingState.failed;
    })
    .addCase(getAICodingJobs.pending, (state) => {
      state.aiCodingJobsLoading = LoadingState.pending;
    })
    .addCase(getAICodingJobs.fulfilled, (state, action) => {
      state.aiCodingJobsLoading = LoadingState.succeed;
      const seen = new Set<number>();
      state.allAiCodingJobs = action.payload.filter((job) => {
        const key = job.jobVariableId;
        if (key === undefined || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    })
    .addCase(getAICodingJobs.rejected, (state) => {
      state.aiCodingJobsLoading = LoadingState.failed;
    })
    .addCase(executeAICodingJob.pending, (state) => {
      state.initiateQuickCodeframeLoading = LoadingState.pending;
    })
    .addCase(executeAICodingJob.fulfilled, (state, action) => {
      state.initiateQuickCodeframeLoading = LoadingState.succeed;
      state.quickCodeframeToken = action.payload.token;
    })
    .addCase(executeAICodingJob.rejected, (state) => {
      state.initiateQuickCodeframeLoading = LoadingState.failed;
    })
    .addCase(getAICodingExecutionStatus.fulfilled, (state, action) => {
      const { status, message } = action.payload;
      state.quickCodeframePollingStatus = status;
      if ((status === AICodingExecutionStatuses.Failed || status === AICodingExecutionStatuses.Unknown) && message) {
        state.quickCodeframeError = message;
      }
    })
    .addCase(getAICodingCodeframe.fulfilled, (state, action) => {
      state.aiCodeframeItems = action.payload;
      state.codeframeCount = action.payload?.length ?? 0;
    })
    .addCase(getCodeframeCodedCount.fulfilled, (state, action) => {
      const codedCount = action.payload ?? 0;
      const jobConfigId = action.meta.arg;
      state.codeframeCodedCountByJobConfigId[jobConfigId] = codedCount;

      if (state.currentAICodingJobConfigId === jobConfigId && state.variableItemVM) {
        state.variableItemVM.stats.coded = codedCount;
        state.variableItemVM.stats.notCoded = state.variableItemVM.stats.total !== undefined
          ? Math.max((state.variableItemVM.stats.total ?? 0) - (state.variableItemVM.stats.coded ?? 0), 0)
          : undefined;
      }
    })
    .addCase(downloadCodeframeResults.pending, (state) => {
      state.downloadCodeframeResultsLoading = LoadingState.pending;
    })
    .addCase(downloadCodeframeResults.fulfilled, (state) => {
      state.downloadCodeframeResultsLoading = LoadingState.succeed;
    })
    .addCase(downloadCodeframeResults.rejected, (state) => {
      state.downloadCodeframeResultsLoading = LoadingState.failed;
    });
}
